"use client";

import { useState, useEffect, useRef } from "react";
import IntlTelInput from "react-intl-tel-input";
// const IntlTelInput = dynamic(() => import("react-intl-tel-input"), { ssr: false });
import style from "./FloatingField.module.css";
import { useTrackingContext } from "@/Context/TrackingContext";
import "react-intl-tel-input/dist/main.css";

const FloatingField = ({
  id,
  label,
  name,
  rows = 4,
  type = "text",
  register,
  rules = {},
  error,
  textarea = false,
  options = [],
  className,
  className1,
  defaultCountry,
  phoneNumber,
  onPhoneNumberChange,
  isValidNum,
  onChange,
  setValue, // 👈 NEW prop
  isFloatedLabel = true,
  required=true,
  placeholder = ""
}) => {
  const { countryCode } = useTrackingContext();

  const intlWrapperRef = useRef(null);

  // We'll trust "register" to handle initial values,
  // but we need local state for the floating label visual.
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);

  /* ===============================
     ✅ Filled detection (autofill-safe)
  ================================ */
  const isIntlFilled = Boolean(phoneNumber && phoneNumber.trim() !== "");

  /* ===============================
     ✅ Robust Autofill Handler (Native Listeners)
     Uses native animationstart and change events to catch autofill even if React misses it.
  ================================ */
  useEffect(() => {
    const el = document.getElementById(id);
    if (!el) return;

    const checkValue = (e) => {
      // If event is animationstart, we only care about autofill
      // In production, the animation name might be hashed, so we just check if it exists.
      const isAnimation = e?.type === "animationstart";

      if (el.value || isAnimation) {
        setIsFilled(!!el.value || isAnimation);

        // Sync with React Hook Form
        if (setValue && name && (el.value || isAnimation)) {
          // Verify we have a value to set, or just trigger validation if animation started
          const val = el.value;
          setValue(name, val, { shouldValidate: true, shouldDirty: true });
        }
      }
    };

    // 1. Immediate check on mount
    checkValue({ type: 'mount' });

    // 2. Listen for CSS animation (Autofill)
    el.addEventListener("animationstart", checkValue);

    // 3. Listen for standard changes (Autofill sometimes fires input/change)
    el.addEventListener("input", checkValue);
    el.addEventListener("change", checkValue);

    return () => {
      el.removeEventListener("animationstart", checkValue);
      el.removeEventListener("input", checkValue);
      el.removeEventListener("change", checkValue);
    };
  }, [id, name, setValue]);

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setIsFilled(value !== "");

    // 🔥 Call parent onChange if provided
    if (typeof onChange === "function") {
      onChange(e);
    }
  };

  /* ===============================
     ✅ Autofill Handling
     Uses CSS animation to detect when browser autofills
  ================================ */
   const handleAutoFill = (e) => {
    const target = e.target;
    // In production, CSS modules might hash the animation name differently than `style.onAutoFillStart`.
    // Since this is the only animation on these inputs, we can safely trust the event.
    if (e.animationName) {
      setIsFilled(true);

      // Ensure RHF knows about the value
      if (setValue && name) {
        setValue(name, target.value, { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  /* ===============================
     ✅ Intl autofill detection
     Autofill does NOT trigger events
  ================================ */
  useEffect(() => {
    if (type === "intl" && intlWrapperRef.current) {
      const input = intlWrapperRef.current.querySelector("input");
      if (input && input.value) {
        setIsFocused(true);
      }
    }
  }, [type]);

  /* ===============================
     ✅ Focus listeners for Intl
  ================================ */
  useEffect(() => {
    if (type !== "intl" || !intlWrapperRef.current) return;

    const wrapper = intlWrapperRef.current;

    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = () => {
      if (!isIntlFilled) {
        setIsFocused(false);
      }
    };

    wrapper.addEventListener("focusin", handleFocusIn);
    wrapper.addEventListener("focusout", handleFocusOut);

    return () => {
      wrapper.removeEventListener("focusin", handleFocusIn);
      wrapper.removeEventListener("focusout", handleFocusOut);
    };
  }, [type, isIntlFilled]);

  return (
    <>
      <div
        className={`${type !== "intl" ? className : ""} ${className1 ? style[className1] : ""} ${isFloatedLabel ? style["floating-group"] : style["non-floating-group"]} ${style["input-group"]}  ${
          error ? style["error"] : ""
        }`}
      >
        {!isFloatedLabel && (
          <label
            htmlFor={id}
            className={`
            ${style["floating-label"]}
            ${type === "intl" ? style["intl-label"] : ""}
            ${isFocused || isFilled || (type === "intl" && isIntlFilled) ? style["active-label"] : ""}
          `}
          >
            {label}
           {required && <span>*</span>}
          </label>
        )}
        {textarea ? (
          <textarea
            id={id}
            name={name}
            {...register(name, {
              ...rules,
              onBlur: (e) => {
                setIsFilled(!!e.target.value);
                if (rules.onBlur) rules.onBlur(e);
              },
            })}
            placeholder={placeholder ? placeholder : ""}
            className={`${style["floating-input"]}`}
            rows={rows}
            onAnimationStart={handleAutoFill}
          />
        ) : type === "select" ? (
          <select
            onChange={handleSelectChange}
            id={id}
            name={name}
            {...register(name, {
              ...rules,
              onChange: (e) => {
                // Update both react-hook-form and local state
                handleSelectChange(e);
                if (rules.onChange) rules.onChange(e);
              },
            })}
            defaultValue=""
            className={`${style["floating-input"]} ${isFilled ? style["filled"] : ""}`}
            onAnimationStart={handleAutoFill}
          >
            <option value="" disabled hidden>{!isFloatedLabel ? placeholder : ""}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : type === "intl" ? (
          <>
            <div ref={intlWrapperRef} className={`${style["intl-wrapper"]} ${className}`}>
              {countryCode && (
                <IntlTelInput
                  numberType={"MOBILE"}
                  autoPlaceholder={isFloatedLabel ? false : true}
                  showFlags={false}
                  separateDialCode={true}
                  preferredCountries={["us", "gb", "in"]}
                  defaultCountry={countryCode}
                  inputClassName={`${style["floating-input"]}`}
                  fieldId={`${id}_intl`}
                  value={phoneNumber}
                  onPhoneNumberChange={onPhoneNumberChange}
                  onPhoneNumberBlur={onPhoneNumberChange}
                />
              )}
            </div>
            {/* Hidden input for React Hook Form */}
            <input type="hidden" id={id} name={name} value={phoneNumber} {...register(name)} />
          </>
        ) : (
          <input
            id={id}
            name={name}
            type={type}
            {...register(name, {
              ...rules,
              onBlur: (e) => {
                setIsFilled(!!e.target.value);
                if (rules.onBlur) rules.onBlur(e);
              },
            })}
            placeholder={placeholder ? placeholder : ""}
            className={`${style["floating-input"]}`}
            onAnimationStart={handleAutoFill}
          />
        )}
        {isFloatedLabel && (
          <label
            htmlFor={id}
            className={`
            ${style["floating-label"]}
            ${type === "intl" ? style["intl-label"] : ""}
            ${isFocused || isFilled || (type === "intl" && isIntlFilled) ? style["active-label"] : ""}
          `}
          >
            {label}
            {required && <span>*</span>}
          </label>
        )}

        {isFloatedLabel && (
          <fieldset className={`${style["fieldsetdiv"]} ${type === "intl" ? style["fieldsetdiv-intl"] : ""} ${textarea ? style["fieldsetdiv-textarea"] : ""}`}>
            <legend className={style["fieldset-legend"]}>{label}</legend>
          </fieldset>
        )}
      </div>
      {error && <p className={`${style["error-text"]}`}>{error.message}</p>}
      {type === "intl" && !error && !isValidNum && <p className={`${style["error-text"]}`}>Please enter valid phone number</p>}
    </>
  );
};

export default FloatingField;
